import React, { useState, useCallback, useEffect } from 'react'
import { ISelectOption } from 'components/forms/Selects'
import { useGlobalToast } from 'transfix-ui/components/Toast'
import { Maybe } from 'types/FMSGraphqlTypes'
import { StringParam } from 'use-query-params'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { useModalState } from 'utils/hooks/useModalState'
import { useSavedLanesContext } from 'apps/fleet-planner/context/SavedLanesContext'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { useQueryParams } from 'utils/hooks'
import { AddCarrierModal, IAddCarrierModalProps } from '../carriers/AddCarrierModal'
import { CarrierProfilesPageWrapper } from './CarrierProfilesPageWrappers'
import CarrierFleetInfoCard from './components/CarrierFleetInfoCard'
import CarrierLoadPreferencesCard from './components/CarrierLoadPreferencesCard'
import CarrierProfileHeader from './components/CarrierProfileHeader'
import { ADD_CARRIER_OPTION_VALUE } from './components/CarrierProfileHeader/components/MotorCarrierSelect'
import RemoveCarrierConfirmationModal, {
  IRemoveCarrierConfirmationModalProps,
} from './components/CarrierProfileHeader/components/RemoveCarrierConfirmationModal'
import DiverseCarrierCertificationsCard from './components/DiverseCarrierCertificationsCard/DiverseCarrierCertificationsCard'
import InsuranceContactInformationCard from './components/InsuranceContactInformationCard/InsuranceContactInformationCard'
import PendingAccessCard from './components/PendingAccessCard'
import SavedLanesSummaryCard from './components/SavedLanesSummaryCard'
import SmartWayCarrierCard from './components/SmartWayCarrierCard'
import { TEST_IDS } from './constants'
import { IMotorCarrierInfoFragment } from './graphql/MotorCarrierInfoFragment'
import { IMotorCarrierUserInfoFragment } from './graphql/MotorCarrierUserInfoFragment'
import { useUsersMotorCarriersQuery } from './graphql/UsersMotorCarriers'
import { useCarrierProfilePageStyles } from './useCarrierProfilesPageStyles'
import { getMotorCarrierSelectOptions, getMotorCarriersList, isJoinRequestPending } from './utils'

export interface ICarrierProfilesPageParams {
  /**
   * This is the fleet planner id of the motor carrier
   */
  carrierId?: number
  mobileApp?: boolean
}

const CarrierProfilesPage = () => {
  const classes = useCarrierProfilePageStyles()
  const isLoadPreferencesFlagEnabled = useFeatureFlag('motorCarrierProfileShowLoadPreferencesCard')
  const isCertificationsFlagEnabled = useFeatureFlag(
    'motorCarrierProfileShowDiverseCarrierCertificationsCard'
  )
  const isSmartWayCarrierEnabled = useFeatureFlag('motorCarrierProfileShowSmartWayCarrierCard')
  const isSavedLanesEnabled = useFeatureFlag('motorCarrierProfileShowSavedLanesCard')

  const { openToast } = useGlobalToast()
  const { trackEvent } = useFMSAnalytics()

  const [{ carrierId }] = useQueryParams<ICarrierProfilesPageParams>({ carrierId: StringParam })
  // Currently selected MotorCarrier ID
  const [motorCarrierId, setMotorCarrierId] = useState<IMotorCarrierInfoFragment['id']>()

  // Fetch user's saved lanes
  const { savedLanes = [] } = useSavedLanesContext()

  const filteredSavedLanes = savedLanes.filter(
    lane => lane.motorCarrier?.id.toString() === motorCarrierId
  )

  // When motorCarrierId changes - track view event
  useEffect(() => {
    if (motorCarrierId) {
      trackEvent('Carrier Profile', 'VIEWED_CARRIER_PROFILE', { motorCarrierId })
    }
  }, [motorCarrierId])

  // Modals State
  const { createToggleHandler, modalState } = useModalState({
    addCarrierModal: false,
    removeCarrierModal: false,
  })
  const { addCarrierModal: isAddCarrierModalOpen, removeCarrierModal: isRemoveCarrierModalOpen } =
    modalState || {}
  const toggleAddCarrierModalHandler = createToggleHandler('addCarrierModal')
  const toggleRemoveCarrierModalHandler = createToggleHandler('removeCarrierModal')

  // Users MotorCarriers Query
  // If a carrierId is in the query, it will be selected first
  // If the id is invalid, it defaults back to the first motorCarrier in the list
  const { data, refetch } = useUsersMotorCarriersQuery({
    onCompleted: data => {
      if (!motorCarrierId) {
        const motorCarriers = getMotorCarriersList(data)
        const carrierFromParams = motorCarriers.find(carrier => carrier?.id === String(carrierId))
        const [firstMotorCarrier] = motorCarriers || []
        const { id: firstMotorCarrierId } = carrierFromParams || firstMotorCarrier || {}
        setMotorCarrierId(firstMotorCarrierId)
      }
    },
  })
  const {
    motorCarrierJoinRequestsForUser: joinRequests = [],
    motorCarrierUsersForUser: motorCarrierUsers = [],
  } = data || {}

  // List of MotorCarriers associated with user and MotorCarriers with pending join requests
  const allMotorCarriers = getMotorCarriersList(data)

  // List of MotorCarriers dot numbers
  const allMotorCarriersDotNumbers = allMotorCarriers.map(carrier => carrier.dotNumber)

  // Currently selected MotorCarrier
  const motorCarrier: Maybe<IMotorCarrierInfoFragment> = allMotorCarriers.find(
    ({ id }) => id === motorCarrierId
  )
  const { state } = motorCarrier || {}

  // Currently selected MotorCarrierUser
  const motorCarrierUser: Maybe<IMotorCarrierUserInfoFragment> = motorCarrierUsers.find(
    ({ motorCarrier }) => motorCarrier.id === motorCarrierId
  )
  const { createdAt: userAddedAt } = motorCarrierUser || {}

  // Determines if current MotorCarrier has a pending join request
  const isJoinRequest = isJoinRequestPending({ motorCarrierId, joinRequests })

  // Determines if profile info cards should display
  const showInfoCards = !isJoinRequest && !!state

  // Generates list of MotorCarrier options to display in dropdown
  const motorCarrierOptions = getMotorCarrierSelectOptions(allMotorCarriers)

  // Determines if there are multiple motor carriers to display
  const hasMultipleCarriers = allMotorCarriers.length > 1

  const motorCarrierSelectHandler = useCallback(
    (selectedMotorCarrier?: ISelectOption) => {
      const { value } = selectedMotorCarrier || {}
      if (value === ADD_CARRIER_OPTION_VALUE) {
        toggleAddCarrierModalHandler()
      } else {
        setMotorCarrierId(value)
      }
    },
    [toggleAddCarrierModalHandler]
  )

  const addMotorCarrierSuccessHandler: IAddCarrierModalProps['onSuccess'] = useCallback(() => {
    trackEvent('Carrier Profile', 'ADDED_CARRIER')
    openToast({ toastMessage: 'Carriers added successfully' })
    refetch()
    toggleAddCarrierModalHandler()
  }, [openToast, refetch, toggleAddCarrierModalHandler, trackEvent])

  const removeMotorCarrierSuccessHandler: IRemoveCarrierConfirmationModalProps['onSuccess'] = useCallback(() => {
    trackEvent('Carrier Profile', 'REMOVED_CARRIER')
    refetch()
    toggleRemoveCarrierModalHandler()
  }, [refetch, toggleRemoveCarrierModalHandler, trackEvent])

  return (
    <CarrierProfilesPageWrapper>
      {motorCarrier && (
        <>
          <CarrierProfileHeader
            {...motorCarrier}
            userAddedAt={userAddedAt}
            isJoinRequestPending={isJoinRequest}
            motorCarrierSelectOptions={motorCarrierOptions}
            onCarrierSelect={motorCarrierSelectHandler}
            onAddCarriers={toggleAddCarrierModalHandler}
            onRemoveCarrier={toggleRemoveCarrierModalHandler}
          />
          {isJoinRequest && <PendingAccessCard />}
          {showInfoCards && (
            <div className={classes.cardsContainer} data-test={TEST_IDS.infoCards}>
              <CarrierFleetInfoCard {...motorCarrier} />
              {isLoadPreferencesFlagEnabled && <CarrierLoadPreferencesCard {...motorCarrier} />}
              {isSmartWayCarrierEnabled && <SmartWayCarrierCard {...motorCarrier} />}
              {isCertificationsFlagEnabled && (
                <DiverseCarrierCertificationsCard {...motorCarrier} />
              )}
              {isSavedLanesEnabled && (
                <SavedLanesSummaryCard savedLanes={filteredSavedLanes.length} />
              )}
              <InsuranceContactInformationCard motorCarrierId={motorCarrierId} />
            </div>
          )}
        </>
      )}
      {isRemoveCarrierModalOpen && motorCarrierId && (
        <RemoveCarrierConfirmationModal
          hasMultipleCarriers={hasMultipleCarriers}
          motorCarrierId={motorCarrierId}
          onClose={toggleRemoveCarrierModalHandler}
          onSuccess={removeMotorCarrierSuccessHandler}
        />
      )}
      {isAddCarrierModalOpen && (
        <AddCarrierModal
          onSuccess={addMotorCarrierSuccessHandler}
          onClose={toggleAddCarrierModalHandler}
          initialCarriersDotNumbers={allMotorCarriersDotNumbers}
        />
      )}
    </CarrierProfilesPageWrapper>
  )
}

export default CarrierProfilesPage
